import { useState } from "react";
import { Mail, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { sendEmail } from "@/lib/api";
import { fmtMoney } from "@/lib/format";

const buildHtml = (result) => {
  const v = result.vehicle || {};
  const disclosures = result.disclosures || [];
  const row = (l, s) =>
    `<tr><td style="padding:8px 12px;border:1px solid #e2e8f0;font-family:Arial;font-size:13px;">${l}</td>
     <td style="padding:8px 12px;border:1px solid #e2e8f0;font-family:Arial;font-size:13px;font-weight:bold;">${s}</td></tr>`;
  return `
  <div style="max-width:640px;margin:0 auto;font-family:Arial,sans-serif;color:#0F172A;">
    <h2 style="font-size:20px;">Contract Cop — Used-Car Contract Report</h2>
    <p style="font-size:14px;">Vehicle: ${[v.year, v.make, v.model].filter(Boolean).join(" ") || "—"} · VIN: ${v.vin || "—"}</p>
    <p style="font-size:14px;">Dealer: ${v.dealer_name || "—"}</p>
    <div style="font-size:32px;font-weight:bold;">Compliance Score: ${result.compliance_score}/100</div>
    <table style="border-collapse:collapse;margin:16px 0;width:100%;">
      ${disclosures.map((d) => row(d.title, d.status)).join("")}
    </table>
    <p style="font-size:14px;"><b>Contract all-in (pre-tax):</b> ${fmtMoney(result.contract_all_in_pre_tax)}</p>
    ${result.price_mismatch_detected
      ? `<p style="font-size:14px;color:#DC2626;"><b>Cheaper online:</b> ${fmtMoney(result.lowest_online_price_found)} (${fmtMoney(result.discrepancy_amount)} lower)</p>`
      : `<p style="font-size:14px;">No cheaper verifiable live listing found.</p>`}
    <h3 style="font-size:16px;">Buyer Action Script</h3>
    <pre style="white-space:pre-wrap;font-family:Arial;font-size:13px;background:#f1f5f9;padding:16px;border-radius:8px;">${result.buyer_action_script}</pre>
    <p style="font-size:11px;color:#64748b;margin-top:24px;line-height:1.6;">
      <b style="color:#0F172A;">LIMITATION OF LIABILITY:</b> THIS APP IS NOT AFFILIATED WITH THE ONTARIO
      MOTOR VEHICLE INDUSTRY COUNCIL OR SERVICE ONTARIO. THE DEVELOPERS AND OPERATORS OF THIS PLATFORM ACCEPT
      ZERO LIABILITY FOR ANY FINANCIAL LOSSES, BAD VEHICLE PURCHASES OR UNEXPECTED LEGAL OBLIGATIONS
      RESULTING DIRECTLY OR INDIRECTLY FROM THE USE OR MISINTERPRETATION OF THIS TOOL. VERIFY YOUR CONTRACT
      WITH A LICENSED ONTARIO LEGAL PROFESSIONAL OR CONSUMER ADVOCATE BEFORE SIGNING.
    </p>
  </div>`;
};

export const EmailResults = ({ result }) => {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await sendEmail(
        email,
        `Contract Cop Report — Score ${result.compliance_score}/100`,
        buildHtml(result)
      );
      toast.success(`Report sent to ${email}`);
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d?.msg || "Invalid input").join(", ")
        : detail || err?.message || "Failed to send email.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="email-results" className="flex h-full flex-col rounded-xl border border-border bg-card p-6">
      <div className="flex items-center gap-2">
        <Mail className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
        <h3 className="font-heading text-lg font-800 tracking-tight text-foreground">Email this report</h3>
      </div>
      <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
        Send a copy to yourself before you talk to the dealer. Nothing is stored after this session.
      </p>
      <form onSubmit={submit} className="mt-4 flex flex-col gap-3 sm:flex-row">
        <Input
          data-testid="email-input"
          type="email"
          required
          placeholder="you@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1"
        />
        <Button data-testid="send-email-btn" type="submit" disabled={sending} className="rounded-full px-6">
          {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
          {sending ? "Sending" : "Send"}
        </Button>
      </form>
    </div>
  );
};
